import { PageContainer } from '@amelio/ui-library';

import { DashboardFilters, type DashboardFiltersProps } from './DashboardFilters/DashboardFilters';
import { Header, type HeaderCard } from './Header/Header';
import { OngoingReviews } from './OngoingReviews/OngoingReviews';
import { PastReviews } from './PastReviews/PastReviews';
import { ReviewsToBeLaunched } from './ReviewsToBeLaunched/ReviewsToBeLaunched';

import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';
import type { OptionProps } from '@amelio/ui-library';

import {
    OngoingPastArrowIllustration,
    ToBeLaunchedOngoingArrowIllustration,
} from '@/components/Illustrations/Illustration';
import { useAmelioTranslations } from '@/hooks';

export interface DashboardProps {
    reviewsToBeLaunched: CycleInstance[];
    reviewsToBeLaunchedLoading: boolean;
    reviewsToBeLaunchedCurrentPage: number;
    reviewsToBeLaunchedTotalCount: number;
    reviewsToBeLaunchedTotalPages: number;
    onReviewsToBeLaunchedPageChange: (page: number) => void;
    ongoingReviews: ReviewInstance[];
    ongoingReviewsLoading: boolean;
    ongoingReviewsCurrentPage: number;
    ongoingReviewsTotalCount: number;
    ongoingReviewsTotalPages: number;
    onOngoingReviewsPageChange: (page: number) => void;
    pastReviews: ReviewInstance[];
    pastReviewsLoading: boolean;
    pastReviewsCurrentPage: number;
    pastReviewsTotalCount: number;
    pastReviewsTotalPages: number;
    onPastReviewsPageChange: (page: number) => void;
    createCycleInstanceIsPending: boolean;
    users: User[];
    profileNameByUserId?: Record<string, string>;
    buttonOptions: OptionProps[];
    reviewsToBeLaunchedActions: (cycleInstance: CycleInstance) => OptionProps[];
    ongoingReviewsActions: (reviewInstance: ReviewInstance) => OptionProps[];
    onPrepareForLaunchClick: (cycleInstance: CycleInstance) => void;
    onViewResults: (reviewInstance: ReviewInstance) => void;
    filterProps: DashboardFiltersProps;
    hasActiveFilters: boolean;
}

export const Dashboard = ({
    reviewsToBeLaunched,
    reviewsToBeLaunchedLoading,
    reviewsToBeLaunchedCurrentPage,
    reviewsToBeLaunchedTotalCount,
    reviewsToBeLaunchedTotalPages,
    onReviewsToBeLaunchedPageChange,
    ongoingReviews,
    ongoingReviewsLoading,
    ongoingReviewsCurrentPage,
    ongoingReviewsTotalCount,
    ongoingReviewsTotalPages,
    onOngoingReviewsPageChange,
    pastReviews,
    pastReviewsLoading,
    pastReviewsCurrentPage,
    pastReviewsTotalCount,
    pastReviewsTotalPages,
    onPastReviewsPageChange,
    createCycleInstanceIsPending,
    users,
    profileNameByUserId = {},
    buttonOptions,
    reviewsToBeLaunchedActions,
    onPrepareForLaunchClick,
    ongoingReviewsActions,
    onViewResults,
    filterProps,
    hasActiveFilters,
}: DashboardProps) => {
    const { t } = useAmelioTranslations();

    const headerCards: HeaderCard[] = [
        {
            icon: 'programming-hierarchy',
            label: t('evaluation360Dashboard.header.collect.title'),
            description: t('evaluation360Dashboard.header.collect.description'),
        },
        {
            icon: 'edit-clipboard-tick',
            label: t('evaluation360Dashboard.header.review.title'),
            description: t('evaluation360Dashboard.header.review.description'),
        },
    ];

    return (
        <PageContainer className={'evaluation360-dashboard-layout'}>
            <Header headerCards={headerCards} />

            {filterProps.companyAttributes.length > 0 && <DashboardFilters {...filterProps} />}

            <ReviewsToBeLaunched
                reviewsToBeLaunched={reviewsToBeLaunched}
                isLoading={reviewsToBeLaunchedLoading}
                currentPage={reviewsToBeLaunchedCurrentPage}
                totalCount={reviewsToBeLaunchedTotalCount}
                totalPages={reviewsToBeLaunchedTotalPages}
                onPageChange={onReviewsToBeLaunchedPageChange}
                createCycleInstanceIsPending={createCycleInstanceIsPending}
                buttonOptions={buttonOptions}
                users={users}
                profileNameByUserId={profileNameByUserId}
                onPrepareForLaunchClick={onPrepareForLaunchClick}
                actionMenuItems={reviewsToBeLaunchedActions}
                hasActiveFilters={hasActiveFilters}
            />

            {(ongoingReviewsTotalCount > 0 || ongoingReviewsLoading || hasActiveFilters) && (
                <>
                    <ToBeLaunchedOngoingArrowIllustration
                        className={
                            'relative z-2 hidden sm:left-1/8 sm:!block sm:!h-72 sm:!w-72 md:left-1/4 md:-mt-12 md:!h-128 md:!w-128'
                        }
                    />

                    <OngoingReviews
                        ongoingReviews={ongoingReviews}
                        isLoading={ongoingReviewsLoading}
                        currentPage={ongoingReviewsCurrentPage}
                        totalCount={ongoingReviewsTotalCount}
                        totalPages={ongoingReviewsTotalPages}
                        onPageChange={onOngoingReviewsPageChange}
                        users={users}
                        actionMenuItems={ongoingReviewsActions}
                        onViewResults={onViewResults}
                        hasActiveFilters={hasActiveFilters}
                    />
                </>
            )}

            {(pastReviewsTotalCount > 0 || pastReviewsLoading || hasActiveFilters) && (
                <>
                    <OngoingPastArrowIllustration
                        className={
                            'relative z-10 -mt-8 hidden sm:left-1/8 sm:!block sm:!h-72 sm:!w-72 md:left-1/5 md:-mt-12 md:!h-128 md:!w-128 lg:left-1/3'
                        }
                    />

                    <PastReviews
                        pastReviews={pastReviews}
                        isLoading={pastReviewsLoading}
                        currentPage={pastReviewsCurrentPage}
                        totalCount={pastReviewsTotalCount}
                        totalPages={pastReviewsTotalPages}
                        onPageChange={onPastReviewsPageChange}
                        users={users}
                        profileNameByUserId={profileNameByUserId}
                        onViewResults={onViewResults}
                        hasActiveFilters={hasActiveFilters}
                    />
                </>
            )}
        </PageContainer>
    );
};
